import { useEffect, useState } from 'react';
import {
  listElectronicDeliveryProfiles,
  transitionElectronicDeliveryProfile,
  type ElectronicDeliveryProfile,
  type ElectronicProfileType,
} from './api';

type Props = {
  csrfToken: string;
  onError: (error: unknown) => void;
};

function profileLabel(value: ElectronicProfileType): string {
  if (value === 'HOKKAIDO_DEVELOPMENT_BUREAU') return '北海道開発局';
  if (value === 'HOKKAIDO_CONSTRUCTION_DEPARTMENT') return '北海道建設部';
  return '市町村（共通）';
}

function ProfileCard({
  busy,
  onTransition,
  profile,
}: {
  busy: boolean;
  onTransition: (
    profile: ElectronicDeliveryProfile,
    action: 'APPROVE' | 'RETIRE' | 'SUBMIT_REVIEW',
    reason: string,
  ) => Promise<void>;
  profile: ElectronicDeliveryProfile;
}) {
  const [reason, setReason] = useState(
    '公式情報の入口・対象期間・注意文を確認し、運用上の判断として処理します。',
  );
  const action =
    profile.status === 'DRAFT'
      ? 'SUBMIT_REVIEW'
      : profile.status === 'IN_REVIEW'
        ? 'APPROVE'
        : profile.status === 'APPROVED'
          ? 'RETIRE'
          : null;
  const actionLabel =
    action === 'SUBMIT_REVIEW'
      ? 'レビューへ送る'
      : action === 'APPROVE'
        ? '公式情報を確認して承認'
        : action === 'RETIRE'
          ? 'この版を廃止'
          : null;
  return (
    <article className="card stack profile-card">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{profile.profileType}</p>
          <h3>{profileLabel(profile.profileType)}</h3>
        </div>
        <span className={`status-chip ${profile.status.toLowerCase()}`}>{profile.status}</span>
      </div>
      <dl className="detail-list compact-list">
        <div>
          <dt>版</dt>
          <dd>{profile.version}</dd>
        </div>
        <div>
          <dt>有効期間</dt>
          <dd>
            {profile.effectiveFrom}〜{profile.effectiveTo ?? '継続'}
          </dd>
        </div>
        <div>
          <dt>root folder</dt>
          <dd>{profile.rules.rootFolderTemplate}</dd>
        </div>
        <div>
          <dt>確認者</dt>
          <dd>{profile.verifiedByName ?? '未承認'}</dd>
        </div>
      </dl>
      <p>{profile.notes}</p>
      <a href={profile.sourceUrl} rel="noreferrer" target="_blank">
        公式情報の確認入口を開く
      </a>
      {action !== null && (
        <>
          <label>
            判断記録
            <textarea onChange={(event) => setReason(event.target.value)} rows={2} value={reason} />
          </label>
          <button
            className={action === 'RETIRE' ? 'danger ghost' : undefined}
            disabled={busy || reason.trim().length < 10}
            onClick={() => {
              const question =
                action === 'APPROVE'
                  ? '公式情報の入口、有効期間、注意文を人間が確認しましたか？承認しても電子成果品全体の適合保証にはなりません。'
                  : `${actionLabel ?? '状態変更'}を実行しますか？`;
              if (!window.confirm(question)) return;
              void onTransition(profile, action, reason);
            }}
            type="button"
          >
            {actionLabel}
          </button>
        </>
      )}
    </article>
  );
}

export function ElectronicDeliveryProfilesPanel({ csrfToken, onError }: Props) {
  const [profiles, setProfiles] = useState<ElectronicDeliveryProfile[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    setProfiles(await listElectronicDeliveryProfiles());
  }

  useEffect(() => {
    void refresh().catch(onError);
  }, []);

  async function transition(
    profile: ElectronicDeliveryProfile,
    action: 'APPROVE' | 'RETIRE' | 'SUBMIT_REVIEW',
    reason: string,
  ): Promise<void> {
    setBusy(true);
    setNotice(null);
    try {
      await transitionElectronicDeliveryProfile(profile.id, { action, reason }, csrfToken);
      await refresh();
      setNotice(
        action === 'APPROVE'
          ? `${profileLabel(profile.profileType)} ${profile.version}を承認しました。`
          : 'profileの状態を更新しました。',
      );
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="stack">
      <div className="section-heading">
        <div>
          <p className="eyebrow">ELECTRONIC DELIVERY PROFILES</p>
          <h2>電子納品プロファイル</h2>
        </div>
        <button className="secondary" disabled={busy} onClick={() => void refresh()} type="button">
          更新
        </button>
      </div>
      <div className="notice warning">
        当該工事の事前協議および最新版電子納品要領を確認してください。本システムは電子成果品全体への適合を保証するものではありません。
      </div>
      <p className="muted">
        DRAFT → IN_REVIEW → APPROVED → RETIRED
        の順に管理します。成果物には承認時点の版をsnapshotし、後日の改訂で変更しません。
      </p>
      {notice !== null && <div className="notice success">{notice}</div>}
      <div className="content-grid">
        {profiles.map((profile) => (
          <ProfileCard busy={busy} key={profile.id} onTransition={transition} profile={profile} />
        ))}
      </div>
    </section>
  );
}
